import classNames from 'classnames';
import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { FieldTextInput, PrimaryButton } from '../../components';
import * as validators from '../../util/validators';

import { sendEmail } from '../../util/api';
import css from './ContactForm.module.css';

const ContactFormComponent = props => {
  const [loader, setLoader] = useState(false);

  const { rootClassName, className, submitButtonText, placeholderButtonText, ...restProps } = props;

  const handleSubmit = async (values, form) => {
    try {
      setLoader(true);
      const { name, email, inquiry, discordUser } = values;

      const htmlContent = `
    <p><b>Name</b> ${name.trim()}</p>
    <p><b>Email:</b> ${email.trim()}</p>
    <p><b>Discord Username:</b> ${discordUser.trim()}</p>
    <p><b>Inquiry:</b> ${inquiry.trim()}</p>
  `;

      await sendEmail({
        subject: 'New inquiry via contact us page',
        htmlContent,
      });

      window.alert('Message sent successfully');
    } catch (error) {
      console.log('Error sending email:', error);
    } finally {
      setLoader(false);
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <FinalForm
      {...restProps}
      onSubmit={handleSubmit}
      render={fieldRenderProps => {
        const {
          handleSubmit: formHandleSubmit,
          invalid,
          pristine,
          submitting,
          form,
        } = fieldRenderProps;

        const submitDisabled = invalid || pristine || submitting || loader;

        const nameRequired = validators.required('Name is required');
        const emailRequired = validators.required('Email is required');
        const emailValid = validators.emailFormatValid('Please enter a valid email address');
        const inquiryRequired = validators.required(
          'Please enter your inquiry or support question'
        );
        const discordUserRequired = validators.required('Discord username is required');

        return (
          <form
            className={classes}
            onSubmit={e => {
              e.preventDefault();
              formHandleSubmit(e, form);
            }}
          >
            <div className={css.formHeader}>
              <h2 className={css.title}>Contact Us</h2>
              <p className={css.description}>
                Get in touch with us. We'd be happy to hear from you.
              </p>
            </div>

            <div className={css.formFields}>
              <FieldTextInput
                className={css.field}
                type="text"
                id="contactForm.name"
                name="name"
                label="Name"
                placeholder="Your name"
                validate={nameRequired}
              />

              <FieldTextInput
                className={css.field}
                type="email"
                id="contactForm.email"
                name="email"
                label="Email"
                placeholder="your.email@example.com"
                validate={validators.composeValidators(emailRequired, emailValid)}
              />

              <FieldTextInput
                className={css.field}
                type="text"
                id="contactForm.discordUsername"
                name="discordUser"
                label="Discord Username"
                placeholder="Your Discord username"
                validate={discordUserRequired}
              />

              <FieldTextInput
                className={css.field}
                type="textarea"
                id="contactForm.inquiry"
                name="inquiry"
                label="Message"
                placeholder="Tell us how we can help you..."
                validate={inquiryRequired}
              />
            </div>

            <div className={css.submitContainer}>
              <PrimaryButton
                type="submit"
                className={css.submitButton}
                inProgress={loader || submitting}
                disabled={submitDisabled}
              >
                {loader || submitting
                  ? placeholderButtonText || 'Sending...'
                  : submitButtonText || 'Send Message'}
              </PrimaryButton>
            </div>
          </form>
        );
      }}
    />
  );
};

/**
 * Contact form component for collecting user inquiries and support questions
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.submitButtonText custom text for submit button
 * @param {string?} props.placeholderButtonText custom text for submit button when in progress
 * @returns {JSX.Element} contact form component
 */
const ContactForm = props => <ContactFormComponent {...props} />;

export default ContactForm;
